import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowRight, Building2, Briefcase, MapPin, ChevronRight } from 'lucide-react';
import { getCityBySlug, getAllCitySlugs } from '../data/cities';
import { SOLUTION_CATEGORIES, SECTOR_GROUPS } from '../data/homeMenus';

const CityPage = () => {
  const { city } = useParams();
  const cityData = getCityBySlug(city);
  const allCities = getAllCitySlugs();
  const otherCities = allCities.filter(c => c !== city);

  // Redirect to 404 if city doesn't exist
  if (!cityData) {
    return <Navigate to="/" replace />;
  }

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-36 pb-20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(16,185,129,0.15),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(59,130,246,0.1),transparent_50%)]"></div>
        
        <div className="container mx-auto px-4 relative">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-8">
            <Link to="/" className="hover:text-emerald-400 transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/locations" className="hover:text-emerald-400 transition-colors">Locations</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{cityData.name}</span>
          </nav>

          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-emerald-600/20 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-emerald-400 text-sm font-semibold">{cityData.state}</p>
                <h1 className="text-4xl lg:text-5xl font-bold text-white">{cityData.heroTitle}</h1>
              </div>
            </div>

            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              {cityData.heroDescription}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-6 h-auto"
              >
                Discuss Your Financing Requirement
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={() => scrollToSection('capital-solutions')}
                size="lg"
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white/10 px-8 py-6 h-auto"
              >
                Explore Capital Solutions
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* City Context */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          {cityData.intro.map((paragraph, idx) => (
            <p key={idx} className="text-lg text-slate-700 leading-relaxed mb-4">
              {paragraph}
            </p>
          ))}
        </div>
      </section>

      {/* Capital Solutions */}
      <section id="capital-solutions" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Capital Solutions in {cityData.name}
            </h2>
            <p className="text-lg text-slate-600">
              Comprehensive financing solutions tailored for businesses in {cityData.name}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {SOLUTION_CATEGORIES.map((category) => (
              <Link
                key={category.name}
                to={`/solutions/${category.slug}`}
                className="group p-6 bg-white rounded-xl border border-slate-200 hover:border-emerald-500 hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">
                  <Building2 className="w-10 h-10 text-emerald-600 group-hover:scale-110 transition-transform" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-emerald-700 transition-colors">
                  {category.name}
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  {category.items?.length || 0} solutions available
                </p>
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>View Solutions</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Business Sectors */}
      <section id="business-sectors" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Industries & Business Sectors in {cityData.name}
            </h2>
            <p className="text-lg text-slate-600">
              Specialized financing solutions across key industries
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {SECTOR_GROUPS.map((sector) => (
              <div key={sector.name} className="group">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{sector.name}</h3>
                    <ul className="space-y-2">
                      {sector.items.map((item) => (
                        <li key={item[0]}>
                          <Link
                            to={item[1]}
                            className="text-sm text-slate-600 hover:text-emerald-700 flex items-center gap-2 group/item"
                          >
                            <ChevronRight className="w-4 h-4 text-emerald-500 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                            <span>{item[0]}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">
            Frequently Asked Questions - {cityData.name}
          </h2>
          <div className="space-y-6">
            {cityData.faqs.map((faq, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl border border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-3">{faq.question}</h3>
                <p className="text-slate-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Cities */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
            Explore Other Cities
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 max-w-6xl mx-auto">
            {otherCities.map((citySlug) => {
              const otherCity = getCityBySlug(citySlug);
              return (
                <Link
                  key={citySlug}
                  to={`/locations/${citySlug}`}
                  className="p-4 text-center bg-slate-50 rounded-lg hover:bg-emerald-50 hover:shadow-md transition-all group"
                >
                  <MapPin className="w-5 h-5 text-emerald-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <p className="text-sm font-semibold text-slate-900">{otherCity.name}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CityPage;
